'use client';

import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/api/trpc';

/**
 * Reusable picker for the Stripe Connect `payments_pricing` group
 * assigned to a partner row. Used by both the "Add Partner" wizard
 * and the partner edit page so the AM sees the same control surface
 * in both spots.
 *
 * Source of truth
 * ───────────────
 * The list is fetched live via `partners.listStripePricingGroups` —
 * Stripe doesn't expose a dedicated list endpoint, so the server
 * builds it from the union of DB-side overrides + a sample of the
 * platform's recent Connect accounts + the curated label registry.
 * That means every group an AM has ever pinned to a partner OR that
 * is currently assigned to at least one merchant on Stripe shows up
 * here automatically — no code change required.
 *
 * Selection model
 * ───────────────
 * The picker emits ONE of three intents to the caller:
 *   - `null`            — "use the platform default", i.e. clear any
 *                          per-partner override. The runtime resolver
 *                          (`resolveStripePricingGroup`) will fall back
 *                          to `STANDARD_STRIPE_PRICING_GROUP`.
 *   - `acctgrp_…`       — the explicit group ID to persist.
 *   - `undefined`       — caller asked for the "Custom" branch but
 *                          hasn't typed a valid `acctgrp_…` ID yet.
 *                          The caller should disable submit while it's
 *                          undefined (we surface `isValid` for that).
 *
 * The radio list always renders the discovered groups + a permanent
 * "Custom group ID" escape-hatch row so future tiers don't need a
 * refresh to be selectable. `forceRefresh` re-runs the discovery
 * server-side and bypasses the 5-min in-process cache — useful right
 * after a Stripe Dashboard edit.
 */

const ACCTGRP_RE = /^acctgrp_[A-Za-z0-9]{3,}$/;

export type StripePricingGroupSummary =
  inferRouterOutputs<AppRouter>['partners']['listStripePricingGroups'][number];

export type PickerSelection =
  | { kind: 'standard' }
  | { kind: 'preset'; id: string }
  | { kind: 'custom'; value: string };

export interface StripePricingGroupPickerProps {
  /**
   * The currently-persisted value on the partner row (or `null` if
   * the partner has no override). Used to seed the picker state and
   * to display the "current" badge on the matching row.
   */
  currentValue: string | null;
  /** Called whenever the resolved wire value changes. */
  onChange: (value: string | null | undefined) => void;
  /**
   * Optional override for the standard group ID — defaults to the
   * platform standard (`acctgrp_SujoT0X49KZc8S`). Currently only the
   * test suite uses this knob; production callers leave it.
   */
  standardId?: string;
  /**
   * Disabled state — used by the parent forms while a save is in
   * flight so the AM can't change the picker mid-mutation.
   */
  disabled?: boolean;
}

const DEFAULT_STANDARD = 'acctgrp_SujoT0X49KZc8S';

function selectionFromValue(
  current: string | null,
  groups: StripePricingGroupSummary[],
  standardId: string,
): PickerSelection {
  if (current === null || current === standardId) return { kind: 'standard' };
  if (groups.some((g) => g.id === current)) {
    return { kind: 'preset', id: current };
  }
  return { kind: 'custom', value: current };
}

function selectionToWireValue(
  s: PickerSelection,
  standardId: string,
): string | null | undefined {
  if (s.kind === 'standard') return null;
  if (s.kind === 'preset') return s.id;
  // Custom: only emit when the typed ID is well-formed; otherwise
  // we want the parent to disable submit.
  return ACCTGRP_RE.test(s.value.trim()) ? s.value.trim() : undefined;
}

export function StripePricingGroupPicker({
  currentValue,
  onChange,
  standardId = DEFAULT_STANDARD,
  disabled = false,
}: StripePricingGroupPickerProps) {
  const query = trpc.partners.listStripePricingGroups.useQuery(
    {},
    { staleTime: 5 * 60 * 1000 },
  );

  const groups = query.data ?? [];
  const [selection, setSelection] = React.useState<PickerSelection>(() =>
    selectionFromValue(currentValue, groups, standardId),
  );

  // Re-seed when the loaded list changes — if `currentValue` happens
  // to match a preset we only learned about after the first render,
  // promote the selection from "custom" to that preset so the radio
  // visibly highlights it.
  const initialisedFromList = React.useRef(false);
  React.useEffect(() => {
    if (initialisedFromList.current || groups.length === 0) return;
    initialisedFromList.current = true;
    setSelection(selectionFromValue(currentValue, groups, standardId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  // Whenever the selection changes, push the resolved wire value up
  // to the parent — including `undefined` while a "Custom" entry is
  // invalid, so submit can disable cleanly.
  React.useEffect(() => {
    onChange(selectionToWireValue(selection, standardId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.kind, (selection as any).id, (selection as any).value]);

  const utils = trpc.useUtils();
  const refresh = React.useCallback(async () => {
    // Bypass the server-side 5-min cache; this is the "I just edited
    // a group in the Stripe Dashboard" button.
    await utils.partners.listStripePricingGroups.refetch({ forceRefresh: true });
  }, [utils]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Discovered from Stripe + partner overrides.{' '}
          {query.isFetching ? (
            <span className="italic">Refreshing…</span>
          ) : (
            <>
              {groups.length} group{groups.length === 1 ? '' : 's'} found.
            </>
          )}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || query.isFetching}
          onClick={refresh}
          title="Re-scan Stripe + DB for newly-created pricing groups"
        >
          <RefreshCw className={`mr-1 h-3 w-3 ${query.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
        {query.isLoading ? (
          <div className="text-xs text-muted-foreground">Loading groups…</div>
        ) : null}

        {query.error ? (
          <div className="text-xs text-destructive">
            Failed to load: {query.error.message}. The Custom row below still works
            — paste the <code>acctgrp_…</code> ID directly.
          </div>
        ) : null}

        {groups.map((g) => {
          const checked =
            (g.isStandard && selection.kind === 'standard') ||
            (selection.kind === 'preset' && selection.id === g.id);
          const isCurrent = g.id === currentValue;
          return (
            <label key={g.id} className="flex items-start gap-2">
              <input
                type="radio"
                name="stripePricingGroupPicker"
                className="mt-0.5"
                checked={checked}
                disabled={disabled}
                onChange={() =>
                  setSelection(
                    g.isStandard
                      ? { kind: 'standard' }
                      : { kind: 'preset', id: g.id },
                  )
                }
              />
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">
                    {g.label ?? 'Custom group'}
                  </span>
                  <code className="text-xs text-muted-foreground">{g.id}</code>
                  {isCurrent ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                      current
                    </span>
                  ) : null}
                </div>
                {g.description ? (
                  <div className="text-xs text-muted-foreground">{g.description}</div>
                ) : null}
                {!g.isStandard ? (
                  <div className="text-[11px] text-muted-foreground">
                    {g.partnerOverrideCount} partner override
                    {g.partnerOverrideCount === 1 ? '' : 's'}
                    {' · '}
                    {g.observedOnAccountsSample} account
                    {g.observedOnAccountsSample === 1 ? '' : 's'} on Stripe sample
                  </div>
                ) : null}
              </div>
            </label>
          );
        })}

        {/* The Custom row always renders — it's the escape hatch for
            a freshly-created group that hasn't been assigned to any
            account yet (so the discovery sweep won't see it). */}
        <label className="flex items-start gap-2 border-t pt-2">
          <input
            type="radio"
            name="stripePricingGroupPicker"
            className="mt-0.5"
            checked={selection.kind === 'custom'}
            disabled={disabled}
            onChange={() =>
              setSelection({
                kind: 'custom',
                value:
                  selection.kind === 'custom' ? selection.value : '',
              })
            }
          />
          <div className="flex-1 space-y-1">
            <div className="font-medium">Custom group ID</div>
            <Input
              placeholder="acctgrp_xxxxxxxxxxxx"
              value={selection.kind === 'custom' ? selection.value : ''}
              onChange={(e) =>
                setSelection({ kind: 'custom', value: e.target.value.trim() })
              }
              disabled={disabled || selection.kind !== 'custom'}
              className="font-mono"
            />
            {selection.kind === 'custom' &&
            selection.value !== '' &&
            !ACCTGRP_RE.test(selection.value) ? (
              <p className="text-xs text-destructive">
                Must match <code>acctgrp_[A-Za-z0-9]{'{3,}'}</code>.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                For brand-new groups Stripe just created — they won't appear
                above until at least one merchant is assigned.
              </p>
            )}
          </div>
        </label>
      </div>

      <div className="text-xs text-muted-foreground">
        Current saved value:{' '}
        {currentValue ? (
          <code className="font-mono">{currentValue}</code>
        ) : (
          <em>none — partner uses the platform standard</em>
        )}
      </div>
    </div>
  );
}

/**
 * External validation hook for forms: returns `true` when the picker
 * is in a state the parent can safely submit. Cheaper than letting
 * the parent recompute the regex.
 */
export function isStripePricingPickerValid(
  value: string | null | undefined,
): boolean {
  // `null` = revert to standard, `string` = picked or pasted; both fine.
  // `undefined` = caller chose Custom but hasn't typed a valid ID yet.
  return value !== undefined;
}
